import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Text } from '@mantine/core';
import {
  useRidesMeService,
  RIDES_PROVIDERS,
  RIDES_VERIFICATION_STATUS,
  RIDES_VERIFICATION_TYPE,
  type RidesVerificationView,
} from '@actschurch/ccms-sdk';
import { useDriverProfile, useMemberMe, useMyVerifications, useInvalidateMe, findVerification, isSubmitted, isVerified } from '@/lib/me';
import { useRidesLookups } from '@/lib/lookups';
import { toYmd } from '@/lib/format';
import { ruleCodeCopy } from '@/lib/copy';
import { documentRules, driverStandards, useCopy } from '@/lib/settings';
import { docsUsable, typesFor, useMyDocuments } from '@/lib/documents';
import { StepHeader } from '@/components/ui/StepHeader';
import { StickyActionBar } from '@/components/ui/StickyActionBar';
import { SectionCard } from '@/components/ui/SectionCard';
import { Callout } from '@/components/ui/Callout';
import { LoadingState } from '@/components/ui/LoadingState';
import { ErrorState } from '@/components/ui/ErrorState';
import { IdentityDocumentForm } from '@/components/rides/IdentityDocumentForm';
import { LicenceForm, EMPTY_LICENCE, licenceDraftValid, useLicenceRuleOptions, type LicenceDraft } from '@/components/rides/LicenceForm';
import { ErrorAlert } from '@/components/rides/ErrorAlert';
import { DriverDocuments } from '@/components/flows/DriverDocuments';
import { EditHeader, setupPlan, type SetupPart } from './_shared';
import '@/styles/flows.css';

type Part = 'id' | 'licence' | 'docs';
const PARTS: Part[] = ['id', 'licence', 'docs'];

const needsFix = (v: RidesVerificationView | undefined) =>
  !!v && (v.statusID === RIDES_VERIFICATION_STATUS.Failed || v.statusID === RIDES_VERIFICATION_STATUS.Expired || v.statusID === RIDES_VERIFICATION_STATUS.Revoked);

/**
 * Drive, the checks, one screen each:
 *   id       the ID check (once; may already be done as a rider)
 *   licence  check the licence again after a failed or expired check
 *   docs     the documents the settings ask of drivers (required first, optional folded away)
 * With ?setup=1 (a new driver straight from the profile screens) the steps are numbered and lead to
 * the checklist at the end; otherwise each screen is opened from the checklist and returns to it.
 */
export default function DriveVerifyPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const me = useRidesMeService();
  const driver = useDriverProfile();
  const member = useMemberMe();
  const verifications = useMyVerifications();
  const lookups = useRidesLookups();
  const docs = useMyDocuments();
  const invalidate = useInvalidateMe();
  const [licence, setLicence] = useState<LicenceDraft>(EMPTY_LICENCE);
  const [tried, setTried] = useState(false);
  const { t, settings } = useCopy();
  const licenceOpts = useLicenceRuleOptions();
  const setup = params.get('setup') === '1';

  const idV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId);
  const licV = findVerification(verifications.data, RIDES_VERIFICATION_TYPE.DriversLicence);
  const provider = lookups.data?.settings?.verificationProvider ?? RIDES_PROVIDERS.Manual;
  const docRules = documentRules(settings);
  const docsAsked = docRules.enabled && docsUsable(docs.data) && typesFor(docs.data, 'driver', { clearanceForDrivers: docRules.minorRidesRequireClearedDriver }).length > 0;

  // Freeze what was still to do when the checks opened, so the step count doesn't jump.
  const [frozen, setFrozen] = useState<{ idNeeded: boolean; licenceFix: boolean } | null>(null);
  useEffect(() => {
    if (!frozen && verifications.isSuccess && !verifications.isFetching) setFrozen({ idNeeded: !isSubmitted(idV), licenceFix: needsFix(licV) || !licV });
  }, [frozen, verifications.isSuccess, verifications.isFetching, idV, licV]);
  const flags = frozen ?? { idNeeded: !isSubmitted(idV), licenceFix: needsFix(licV) || !licV };
  const plan: SetupPart[] = setupPlan({ contactAsked: driverStandards(settings).driverEmergency !== 'notAsked', idNeeded: flags.idNeeded, licenceFix: flags.licenceFix, docsAsked });
  const checks = plan.filter((p) => p === 'id' || p === 'licence-check' || p === 'docs');

  const resubmitLicence = useMutation({
    mutationFn: () =>
      me.startVerification(RIDES_VERIFICATION_TYPE.DriversLicence, {
        licenceTypeID: licence.licenceTypeID,
        licenceCodeID: licence.licenceCodeID,
        licenceNumber: licence.licenceNumber.trim(),
        country: licence.country || 'ZAF',
        expiresAt: licence.expiresAt ? toYmd(licence.expiresAt) : null,
        inEnglish: licence.inEnglish,
        hasIdp: licence.hasIdp,
      }),
    onSuccess: () => { setLicence((l) => ({ ...l, licenceNumber: '' })); invalidate(); },
  });

  useEffect(() => { setTried(false); }, [params]);

  if (!driver.isLoading && !driver.error && driver.data === null) return <Navigate to="/drive/profile" replace />;
  const loading = driver.isLoading || verifications.isLoading || lookups.isLoading || (docRules.enabled && docs.isLoading);
  const loadError = driver.error ?? verifications.error ?? null;

  const asked = params.get('part') as Part | null;
  const toPart = (p: SetupPart): Part => (p === 'licence-check' ? 'licence' : (p as Part));
  const defaultPart: Part | null = checks.length ? toPart(checks[0]) : null;
  const part: Part | null = asked && PARTS.includes(asked) ? asked : defaultPart;
  if (!loading && !loadError && !part) return <Navigate to="/drive/profile" replace />;
  if (!loading && part === 'docs' && !docsAsked) return <Navigate to="/drive/profile" replace />;

  const planKey: SetupPart = part === 'licence' ? 'licence-check' : (part ?? 'id');
  const nextInSetup = (): string => {
    const i = plan.indexOf(planKey);
    const nxt = plan[i + 1];
    return nxt && (nxt === 'id' || nxt === 'licence-check' || nxt === 'docs') ? `/drive/verify?setup=1&part=${toPart(nxt)}` : '/drive/profile';
  };
  const prevInSetup = (): string => {
    const i = plan.indexOf(planKey);
    const prv = plan[i - 1];
    if (!prv) return '/drive/profile';
    return prv === 'id' || prv === 'licence-check' || prv === 'docs' ? `/drive/verify?setup=1&part=${toPart(prv)}` : `/drive/profile?part=${prv}`;
  };
  const done = () => navigate(setup ? nextInSetup() : '/drive/profile');

  const titles: Record<Part, string> = { id: 'Check your ID', licence: 'Check your licence again', docs: 'Your documents' };
  const ledes: Record<Part, string> = { id: t('drive.id_intro'), licence: t('drive.licence_check_intro'), docs: t('docs.intro') };

  let body: ReactNode = null;
  let action: ReactNode = null;

  if (part === 'id') {
    const okNow = isSubmitted(idV);
    body = okNow ? (
      <div className="rides-flow-stack">
        <Callout tone={isVerified(idV) ? 'ok' : 'info'} title={isVerified(idV) ? 'Your ID is checked' : 'Your details look right'}>
          {isVerified(idV)
            ? "You don't need to do this again."
            : provider === RIDES_PROVIDERS.Simulated
              ? 'Test mode: documents that pass the checks are approved straight away.'
              : t('drive.id_done_pending')}
        </Callout>
      </div>
    ) : (
      <div className="rides-flow-stack">
        {needsFix(idV) && <Callout tone="bad" title="Your last ID check didn't go through">{idV?.failureCode ? ruleCodeCopy(idV.failureCode) : 'Please enter the details again.'}</Callout>}
        <SectionCard title="Your ID">
          <IdentityDocumentForm
            typeId={RIDES_VERIFICATION_TYPE.GovernmentId}
            expectedSexId={driver.data?.sexID ?? null}
            expectedDob={member.data?.dateOfBirth ?? null}
            onResult={() => invalidate()}
            submitLabel="Check my ID"
          />
        </SectionCard>
      </div>
    );
    action = okNow ? <Button fullWidth onClick={done}>Continue</Button> : null;
  }

  if (part === 'licence') {
    const okNow = isSubmitted(licV);
    body = okNow ? (
      <Callout tone={isVerified(licV) ? 'ok' : 'info'} title={isVerified(licV) ? 'Your licence is checked' : 'Your licence details look right'}>
        {isVerified(licV) ? "You don't need to do this again." : t('verify.pending_message')}
      </Callout>
    ) : (
      <div className="rides-flow-stack">
        {licV && (
          <Callout tone="bad" title="Why it didn't go through">
            {licV.failureCode ? ruleCodeCopy(licV.failureCode, { document: 'licence' }) : 'Some of the details didn\'t match. Please check them against your card.'}
          </Callout>
        )}
        <SectionCard title="Your licence">
          <LicenceForm value={licence} onChange={setLicence} disabled={resubmitLicence.isPending} />
          {tried && !licenceDraftValid(licence, licenceOpts) && <Text c="var(--rides-bad)" fw={600} mt="sm" role="alert">Please fill in the licence code, number and expiry date.</Text>}
          <ErrorAlert error={resubmitLicence.error} title="We couldn't check your licence" />
        </SectionCard>
      </div>
    );
    action = okNow ? (
      <Button fullWidth onClick={done}>Continue</Button>
    ) : (
      <Button fullWidth loading={resubmitLicence.isPending} onClick={() => { setTried(true); if (licenceDraftValid(licence, licenceOpts)) resubmitLicence.mutate(); }}>Check my licence</Button>
    );
  }

  if (part === 'docs') {
    body = <DriverDocuments />;
    action = <Button fullWidth onClick={done}>{setup ? 'Continue' : 'Done'}</Button>;
  }

  return (
    <div className="rides-main rides-flow">
      {setup && part ? (
        <StepHeader step={Math.max(1, plan.indexOf(planKey) + 1)} total={plan.length} title={titles[part]} lede={ledes[part]} backTo={prevInSetup()} />
      ) : (
        <EditHeader title={part ? titles[part] : 'Your checks'} lede={part ? ledes[part] : undefined} />
      )}
      {loading ? (
        <LoadingState label="Loading your checks" count={2} />
      ) : loadError ? (
        <ErrorState title="We couldn't load your checks" error={loadError} onRetry={() => { driver.refetch(); verifications.refetch(); }} />
      ) : (
        <>
          <div className="rides-stepbody" key={part ?? 'none'}>{body}</div>
          {action && <StickyActionBar>{action}</StickyActionBar>}
          {!action && part === 'id' && setup && (
            <div style={{ marginTop: 16 }}>
              <Button variant="subtle" fullWidth onClick={done}>I'll do this later</Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
